import React, { useEffect, useState } from "react";
import { X, ImageIcon, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useSection } from "@/context/SiteContext";
import { resolveImageUrl } from "@/lib/site";

export default function GalleryTeaser() {
  const teaser = useSection("gallery_teaser");
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  // Load on first open
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/gallery");
        setImages(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  // Escape + arrow keys in lightbox
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowRight") setActiveIdx((i) => Math.min(i + 1, images.length - 1));
      if (e.key === "ArrowLeft") setActiveIdx((i) => Math.max(i - 1, 0));
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, images.length]);

  const cover = resolveImageUrl(teaser.cover_url);

  return (
    <>
      <section id="galleri" className="py-8 md:py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="gallery-teaser-open"
            className="group w-full relative overflow-hidden rounded-3xl shadow-xl bg-[var(--text)] text-left focus:outline-none focus:ring-4 focus:ring-[var(--primary)]/30"
          >
            <div className="relative h-44 sm:h-56 md:h-64">
              {cover ? (
                <img
                  src={cover}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-60 group-hover:scale-[1.02] transition-all duration-500"
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center text-white/50">
                  <ImageIcon size={40} />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-[var(--text)]/90 via-[var(--text)]/40 to-transparent" />

              <div className="relative h-full flex items-center justify-between gap-6 px-6 sm:px-10 text-white">
                <div className="space-y-2 max-w-md">
                  <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FFB663]">
                    {teaser.eyebrow || "Galleri"}
                  </span>
                  <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl font-black leading-tight">
                    {teaser.title || "Se mer från oss"}
                  </h2>
                  {teaser.subtitle && (
                    <p className="text-sm sm:text-base text-white/70 max-w-sm">{teaser.subtitle}</p>
                  )}
                </div>
                <span className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#FFB663] text-stone-900 px-5 py-3 font-bold group-hover:bg-white transition-colors">
                  {teaser.cta_text || "Visa galleri"}
                  <ArrowUpRight size={16} />
                </span>
              </div>
            </div>
          </button>
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md grid place-items-center p-4 sm:p-8"
          onClick={() => setOpen(false)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="absolute top-5 right-5 w-10 h-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Stäng"
            data-testid="gallery-close"
          >
            <X size={20} />
          </button>

          <div
            className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {loading ? (
              <div className="text-white/60 text-center py-20">Laddar galleri…</div>
            ) : images.length === 0 ? (
              <div className="text-white/60 text-center py-20">
                <ImageIcon size={42} className="mx-auto mb-3 opacity-50" />
                Inga bilder än. Lägg till dem via admin.
              </div>
            ) : (
              <>
                {/* Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                  {images.map((img, i) => (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => setActiveIdx(i)}
                      data-testid={`gallery-thumb-${i}`}
                      className={`relative aspect-square rounded-xl overflow-hidden bg-white/5 group ${
                        activeIdx === i ? "ring-2 ring-[#FFB663]" : "ring-1 ring-white/10"
                      }`}
                    >
                      <img
                        src={resolveImageUrl(img.url)}
                        alt={img.caption || ""}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </button>
                  ))}
                </div>

                {/* Main image */}
                {images[activeIdx] && (
                  <div className="mt-4 sm:mt-6 relative bg-white/5 rounded-2xl overflow-hidden">
                    <img
                      src={resolveImageUrl(images[activeIdx].url)}
                      alt={images[activeIdx].caption || ""}
                      className="w-full max-h-[65vh] object-contain"
                    />
                    {images[activeIdx].caption && (
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white text-sm">
                        {images[activeIdx].caption}
                      </div>
                    )}
                    {images.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() => setActiveIdx((i) => Math.max(i - 1, 0))}
                          disabled={activeIdx === 0}
                          className="absolute top-1/2 -translate-y-1/2 left-3 w-10 h-10 grid place-items-center rounded-full bg-black/60 hover:bg-black/80 text-white disabled:opacity-30"
                          aria-label="Föregående"
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveIdx((i) => Math.min(i + 1, images.length - 1))}
                          disabled={activeIdx === images.length - 1}
                          className="absolute top-1/2 -translate-y-1/2 right-3 w-10 h-10 grid place-items-center rounded-full bg-black/60 hover:bg-black/80 text-white disabled:opacity-30"
                          aria-label="Nästa"
                        >
                          <ChevronRight size={20} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
